import {useCallback} from 'react';

import type {
  AssetDraft,
  IUseAssetsSubStepParams,
  IUseAssetsSubStepResult,
} from '../financials.types';
import {
  lshCreateLocalId,
  lshParseCurrency,
  lshSanitizeCurrencyInput,
} from '../financials.utils';

export function lshUseAssetsSubStep({
  assetCatalogue,
  setAssetRows,
}: IUseAssetsSubStepParams): IUseAssetsSubStepResult {
  const handleAddAsset = useCallback(() => {
    const first = assetCatalogue[0];
    const draft: AssetDraft = {
      localId: lshCreateLocalId(),
      assetListId: first ? String(first.id) : '',
      assetName: first?.name ?? '',
      assetCategory: first?.assetCategory ?? '',
      assetUnit: first?.assetUnit ?? '',
      assetType: first?.name ?? '',
      description: '',
      year: '',
      condition: '',
      model: '',
      make: '',
      capacity: '',
      value: '',
      pricePerUnit: '',
    };
    setAssetRows((current) => [...current, draft]);
  }, [assetCatalogue, setAssetRows]);

  const handleUpdateAssetRowField = useCallback(
    (rowId: number, field: keyof AssetDraft, value: string) => {
      setAssetRows((current) =>
        current.map((row) => {
          if (row.localId !== rowId) return row;

          if (field === 'assetListId') {
            const option = assetCatalogue.find(
              (item) => String(item.id) === value,
            );
            return {
              ...row,
              assetListId: value,
              assetName: option?.name ?? '',
              assetCategory: option?.assetCategory ?? '',
              assetUnit: option?.assetUnit ?? '',
              assetType: option?.name ?? '',
            };
          }

          if (field === 'value' || field === 'pricePerUnit') {
            return {
              ...row,
              [field]: lshSanitizeCurrencyInput(value),
            } as AssetDraft;
          }

          return {...row, [field]: value} as AssetDraft;
        }),
      );
    },
    [assetCatalogue, setAssetRows],
  );

  const validateAssetRow = useCallback((row: AssetDraft) => {
    if (!row.assetListId) {
      return 'Select an asset from the catalogue.';
    }

    if (!row.value || lshParseCurrency(row.value) <= 0) {
      return 'Enter the total value for this asset.';
    }

    if (!row.condition) {
      return 'Select the asset condition.';
    }

    return null;
  }, []);

  return {
    handleAddAsset,
    handleUpdateAssetRowField,
    validateAssetRow,
  };
}
